import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
  Platform
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { Redirect, useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { SettingsCompanyLogger } from '../../../../utils/logger';
import theme from '../../../../config/theme';
import { globalState, companyActions } from '../../../../state/globalState';
import { observable } from '@legendapp/state';
import { getUserCompany, uploadCompanyLogo, deleteCompanyLogo, updateCompanyProfile } from '../../../../services/userService';
import * as ImagePicker from 'expo-image-picker';
import { databaseService } from '../../../../services/databaseService';
import { APP_CONSTANTS } from '../../../../config/constants';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useWebAlert } from '../../../../components/WebAlert';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImageManipulator from 'expo-image-manipulator';
import { v4 as uuidv4 } from 'uuid';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';
import {
  getActionSheetContainerPaddingBottom,
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { canAccessCompanySettings } from '@/utils/userRoles';

// Logger para a página de configurações da empresa
const logger = SettingsCompanyLogger.Main;

// Estado local da UI para esta página
export const companyUIState = observable({
  isLoading: false,
  isSaving: false,
  editField: null as string | null,
  editData: {
    nome: '',
    cnpj: '',
    email: '',
    telefone: '',
    endereco: '',
    cep: '',
    cidade: '',
    uf: ''
  }
});

// Ações para manipular o estado da UI
export const companyUIActions = {
  setLoading: (v: boolean) => companyUIState.isLoading.set(v),
  setSaving: (v: boolean) => companyUIState.isSaving.set(v),
  setEditField: (field: string | null) => companyUIState.editField.set(field),
  updateEditData: (data: any) => {
    companyUIState.editData.set({
      ...companyUIState.editData.get(),
      ...data
    });
  },
  initFromCompany: (company: any) => {
    if (company) {
      companyUIState.editData.set({
        nome: company.nome || '',
        cnpj: company.cnpj || '',
        email: company.email || '',
        telefone: company.telefone || '',
        endereco: company.endereco || '',
        cep: company.cep || '',
        cidade: company.cidade || '',
        uf: company.uf || ''
      });
    }
  }
};

// Interface para o componente de campo editável
interface EditableFieldProps {
  label: string;
  value: string;
  fieldName: string;
  editable?: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChangeText: (text: string) => void;
  placeholder?: string;
  helpText?: string;
  keyboardType?: any;
}

// Componente de campo editável
const EditableField = observer(({
  label,
  value,
  fieldName,
  editable = true,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onChangeText,
  placeholder,
  helpText,
  keyboardType = 'default'
}: EditableFieldProps) => {
  const currentEditField = companyUIState.editField.get();
  const isCurrentlyEditing = currentEditField === fieldName;
  const editData = companyUIState.editData.get();
  const editValue = editData[fieldName as keyof typeof editData];
  const isSaving = companyUIState.isSaving.get();

  logger.info(`[EditableField] ${fieldName} - currentEditField: ${currentEditField}, isCurrentlyEditing: ${isCurrentlyEditing}, isEditing prop: ${isEditing}`);

  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>{label}</Text>
      </View>
      
      <View style={styles.fieldValueContainer}>
        {isCurrentlyEditing ? (
          <View style={styles.editContainer}>
            <TextInput
              style={styles.input}
              value={editValue as string}
              onChangeText={onChangeText}
              placeholder={placeholder}
              placeholderTextColor={theme.colors.text.tertiary}
              keyboardType={keyboardType}
              autoFocus
            />
            <View style={styles.editActions}>
              <TouchableOpacity 
                style={[styles.editButton, styles.cancelButton]} 
                onPress={onCancel}
                disabled={isSaving}
              >
                <Text style={styles.editButtonText}>Cancelar</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.editButton} 
                onPress={onSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={theme.colors.text.inverse} />
                ) : (
                  <Text style={styles.editButtonText}>Salvar</Text>
                )}
              </TouchableOpacity>
            </View>
            
            {helpText && (
              <Text style={styles.helpText}>{helpText}</Text>
            )}
          </View>
        ) : (
          <View style={styles.displayContainer}>
            <Text style={styles.fieldValue}>{value || 'Não informado'}</Text>
            
            {editable && (
              <TouchableOpacity 
                style={styles.editIconButton} 
                onPress={() => {
                  logger.info(`[EditButton] Botão de edição para ${fieldName} clicado`);
                  onEdit();
                }}
              >
                <MaterialCommunityIcons name="pencil" size={24} color={theme.colors.primary.default} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </View>
  );
});

// Componente de campo de logo da empresa
const LogoField = observer(() => {
  const company = globalState.company.data.get();
  const [uploading, setUploading] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const { showActionSheetWithOptions } = useActionSheet();
  const { showAlert } = useWebAlert();
  const safeAreaInsets = useSafeAreaInsets();
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg);
    }
  };
  
  // Usar o previewUri se disponível, senão priorizar o caminho local e por fim a URL remota
  const uriToShow = previewUri ?? company?.logo_local_path ?? company?.logo_url ?? null;
  
  const handlePickImage = () => {
    showActionSheetWithOptions(
      {
        options: ['Cancelar', 'Tirar foto', 'Galeria'],
        cancelButtonIndex: 0,
        title: 'Selecionar imagem',
        message: 'De onde você quer obter sua foto de perfil?',
        // Estilos baseados no tema da aplicação
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        messageTextStyle: {
          color: theme.colors.text.secondary,
          fontSize: theme.fontSizes.sm,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
          paddingBottom: getActionSheetContainerPaddingBottom(safeAreaInsets.bottom),
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      async (buttonIndex) => {
        if (buttonIndex === 1) {
          // câmera
          await takePhoto();
        }
        if (buttonIndex === 2) {
          // galeria
          await pickFromLibrary();
        }
      }
    );
  };

  const handleRemoveLogo = async () => {
    if (!company?.id) return;
    const companyId = company.id;
    setUploading(true);
    
    try {
      logger.info('Chamando deleteCompanyLogo para companyId: ' + companyId);
      const result = await deleteCompanyLogo(companyId);
      if (!result.success) {
        throw new Error(result.error);
      }
      logger.info('Logo removido com sucesso');
      showMessage('Sucesso', 'Logo removido com sucesso!');
    } catch (err) {
      console.warn('Falha ao remover logo:', err);
      showMessage('Erro', 'Não foi possível remover o logo.');
    } finally {
      setUploading(false);
      setPreviewUri(null);
    }
  };

  const takePhoto = async () => {
    try {
      const outcome = await takePhotoFromCamera({
        mediaTypes: ['images'],
        allowsEditing: false,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'settings_company_logo',
        screen: 'settings/company',
        action: 'take_logo_photo_with_camera',
        entityType: 'company_logo',
        entityId: company?.id ?? null,
      });

      if (outcome.status === 'denied') {
        logger.warn('[takePhoto] Permissao negada ao acessar camera para atualizar logo da empresa');
        showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('[takePhoto] Falha ao abrir camera:', outcome.error);
        showMessage('Erro', 'Não foi possível tirar a foto.');
        return;
      }

      if (outcome.status === 'success' && outcome.result.assets?.length > 0) {
        const selectedImage = outcome.result.assets[0];
        await handleSelectedImage(selectedImage);
      }
    } catch (error) {
      logger.error('Erro ao tirar foto:', error);
      showMessage('Erro', 'Não foi possível tirar a foto.');
    }
  };
  
  const pickFromLibrary = async () => {
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        aspect: [1, 1],
        quality: 0.7,
      }, {
        source: 'settings_company_logo',
        screen: 'settings/company',
        action: 'pick_logo_from_library',
        entityType: 'company_logo',
        entityId: company?.id ?? null,
      });

      if (outcome.status === 'denied') {
        logger.warn('[pickFromLibrary] Permissao negada ao acessar fotos para atualizar logo da empresa');
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.error('Erro ao abrir a galeria:', outcome.error);
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success' || !outcome.result.assets?.length) {
        return;
      }

      const selectedImage = outcome.result.assets[0];
      await handleSelectedImage(selectedImage);
    } catch (error) {
      logger.error('Erro ao selecionar imagem da galeria:', error);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };
  
  const handleSelectedImage = async (asset: ImagePicker.ImagePickerAsset) => {
    if (!company?.id) return;
    const companyId = company.id;

    setUploading(true);

    logger.info(`[handleSelectedImage] Iniciando processamento para asset.uri=${asset.uri}`);

    try {
      let localUri = asset.uri;

      // ---------------- WEB ------------------
      if (Platform.OS === 'web') {
        const base64 = await compressImageWeb(asset.uri);
        localUri = `data:image/jpeg;base64,${base64}`;
        logger.info(`[handleSelectedImage][WEB] Imagem comprimida, tamanho ${(base64.length / 1024).toFixed(1)}KB`);
      }

      // --------------- MOBILE ----------------
      if (Platform.OS !== 'web') {
        const manipulated = await ((ImageManipulator as any).manipulate ?? ImageManipulator.manipulateAsync)(
          asset.uri,
          [{ resize: { width: 800 } }],
          { compress: 0.75, format: ImageManipulator.SaveFormat.JPEG, base64: true }
        );

        const base64Data = manipulated.base64 || '';
        const fileName = `${companyId}-${Date.now()}-${uuidv4()}.jpg`;
        const destPath = `logo-empresa/${fileName}`;
        const fileUri = `${FileSystem.documentDirectory}${destPath}`;

        const dir = `${FileSystem.documentDirectory}logo-empresa/`;
        const dirInfo = await FileSystem.getInfoAsync(dir);
        if (!dirInfo.exists) {
          await FileSystem.makeDirectoryAsync(dir, { intermediates: true });
        }

        await FileSystem.writeAsStringAsync(fileUri, base64Data, { encoding: FileSystem.EncodingType.Base64 });
        logger.info(`[handleSelectedImage][MOBILE] Arquivo salvo em ${fileUri}`);

        try { await FileSystem.deleteAsync(asset.uri, { idempotent: true }); } catch {}

        localUri = fileUri;
      }

      // Preview durante upload
      setPreviewUri(localUri);

      logger.info('chamando uploadCompanyLogo com companyId: ' + companyId + ' e localUri: ' + localUri);
      const result = await uploadCompanyLogo(companyId, localUri);
      if (!result.success) throw new Error(result.error);

      await getUserCompany(companyId);
      showMessage('Sucesso', 'Logo atualizado com sucesso!');
    } catch (err) {
      logger.warn('Falha no upload:', err);
      showMessage('Aviso', 'Imagem salva localmente e será enviada quando estiver online.');
    } finally {
      setUploading(false);
      setPreviewUri(null);
    }
  };
  
  if (!company) return null;
  
  return (
    <View style={styles.fieldRow}>
      <View style={styles.fieldLabelContainer}>
        <Text style={styles.fieldLabel}>Logo</Text>
      </View>
      <View style={styles.fieldValueContainer}>
        <View style={{ width: '100%' }}>
          {/* Container da imagem */}
          <View style={{ alignItems: 'flex-start' }}>
            {uriToShow ? (
              <Image
                source={{ uri: uriToShow }}
                style={styles.logo}
              />
            ) : (
              <View style={[styles.logo, styles.logoPlaceholder]}>
                <MaterialCommunityIcons name="office-building" size={40} color={theme.colors.text.inverse} />
              </View>
            )}
          </View>
          {/* Linha de botões abaixo do logo */}
          <View style={{ flexDirection: 'row', marginTop: theme.spacing.sm }}>
            <TouchableOpacity
              style={styles.changeLogoButton}
              onPress={handlePickImage}
              disabled={uploading}
            >
              {uploading ? (
                <ActivityIndicator size="small" color={theme.colors.primary.default} />
              ) : (
                <Text style={styles.changeLogoText}>Alterar logo</Text>
              )}
            </TouchableOpacity>
            {/* Botão de remover logo */}
            {uriToShow && (
              <TouchableOpacity
                style={[styles.changeLogoButton, { marginLeft: theme.spacing.sm, borderColor: theme.colors.util.error }]}
                onPress={handleRemoveLogo}
                disabled={uploading}
              >
                {uploading ? (
                  <ActivityIndicator size="small" color={theme.colors.util.error} />
                ) : (
                  <Text style={[styles.changeLogoText, { color: theme.colors.util.error }]}>Remover logo</Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </View>
  );
});

// Página principal de configurações da empresa
const CompanyScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const userType = globalState.user.profile.get()?.user_type;
  const hasScreenAccess = canAccessCompanySettings(userType);

  const company = globalState.company.data.get();
  const { showAlert } = useWebAlert();
  
  // Função para mostrar alerta adaptável à plataforma
  const showMessage = (title: string, msg: string) => {
    if (Platform.OS !== 'android') {
      // No web e no iOS, usar o alerta temático do app
      showAlert(title, msg, [{ text: 'OK' }]);
    } else {
      // No Android, manter o Alert nativo
      Alert.alert(title, msg);
    }
  };
  
  useEffect(() => {
    logger.debug('Página de configurações da empresa inicializada');
    logger.info('[CompanyScreen] useEffect - company disponível:', !!company);
    
    // Inicializar dados de edição a partir dos dados da empresa
    if (company) {
      companyUIActions.initFromCompany(company);
      logger.info('[CompanyScreen] editData inicializado:', companyUIState.editData.get());
    }
    
    return () => {
      logger.debug('Página de configurações da empresa desmontada');
      // Limpar estado de edição ao desmontar
      companyUIActions.setEditField(null);
    };
  }, [company]);
  
  // Funções para lidar com a edição de campos
  const handleEditField = (fieldName: string) => {
    logger.info(`[handleEditField] Iniciando edição do campo: ${fieldName}, valor anterior: ${companyUIState.editField.get()}`);
    companyUIActions.setEditField(fieldName);
    logger.info(`[handleEditField] Valor após setEditField: ${companyUIState.editField.get()}`);
  };
  
  const handleCancelEdit = () => {
    logger.info('[handleCancelEdit] Cancelando edição');
    companyUIActions.setEditField(null);
    // Restaurar valores originais
    if (company) {
      companyUIActions.initFromCompany(company);
    }
  };
  
  const handleSaveField = async () => {
    try {
      const editField = companyUIState.editField.get();
      const editData = companyUIState.editData.get();
      const companyId = company?.id;
      
      if (!companyId || !editField) {
        showMessage('Erro', 'Não foi possível identificar os dados a serem atualizados.');
        return;
      }
      
      companyUIActions.setSaving(true);
      
      // Objeto com apenas o campo que está sendo editado
      const updateData: any = {};
      updateData[editField] = editData[editField as keyof typeof editData];
      
      logger.info(`Tentando atualizar o campo ${editField} para a empresa ${companyId}`);
      
      // Seguindo o padrão offline-first:
      // 1. Atualizar no banco de dados local primeiro usando databaseService
      // 2. A atualização local registrará a operação como pendente para sincronização automática
      try {
        // Atualizar no banco local - isso registra automaticamente como pendente para sincronização
        const success = await databaseService.updateItem(
          APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME, 
          companyId, 
          updateData
        );
        
        if (!success) {
          throw new Error('Falha ao atualizar dados localmente');
        }
        
        logger.info(`Campo ${editField} atualizado com sucesso localmente`);
        
        // Atualizar o estado global para refletir as mudanças imediatamente
        const currentCompany = globalState.company.data.get();
        if (currentCompany) {
          companyActions.setCompanyData({
            ...currentCompany,
            ...updateData
          });
          logger.info(`Estado global da empresa atualizado com ${editField}=${updateData[editField]}`);
        }
        
        // Atualização local feita com sucesso
        showMessage('Sucesso', 'Dados atualizados com sucesso!');
        companyUIActions.setEditField(null);
        companyUIActions.setSaving(false);
        
        // A sincronização com o servidor será feita automaticamente pelo SyncService
        // quando houver conexão disponível
      } catch (localError) {
        logger.error('Erro ao atualizar localmente:', localError);

        // Se falhar na atualização local, tenta atualizar diretamente via serviço remoto
        logger.info('Tentando atualização via updateCompanyProfile após falha local');
        const { success, error } = await updateCompanyProfile(companyId, updateData);

        if (!success || error) {
          logger.error('Erro ao atualizar empresa via serviço:', error);
          showMessage('Erro', 'Não foi possível atualizar os dados da empresa.');
          companyUIActions.setSaving(false);
          return;
        }

        logger.info(`Campo ${editField} atualizado com sucesso via serviço`);
        showMessage('Sucesso', 'Dados atualizados com sucesso!');
        companyUIActions.setEditField(null);
        companyUIActions.setSaving(false);
        return;
      }
    } catch (error) {
      logger.error('Exceção ao atualizar empresa:', error);
      showMessage('Erro', 'Ocorreu um erro ao atualizar os dados da empresa.');
      companyUIActions.setSaving(false);
    }
  };
  
  // Navegação para voltar
  const handleGoBack = () => {
    router.back();
  };

  if (!hasScreenAccess) {
    return <Redirect href="/(app)/settings" />;
  }
  
  if (!company) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary.default} />
        <Text style={styles.loadingText}>Carregando informações...</Text>
      </View>
    );
  }
  
  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={handleGoBack}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color={theme.colors.text.inverse} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Empresa</Text>
        <View style={styles.headerRight} />
      </View>
      
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <LogoField />
          
          <EditableField
            label="Nome"
            value={company.nome ?? ''}
            fieldName="nome"
            isEditing={true}
            onEdit={() => handleEditField('nome')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ nome: text })}
            placeholder="Nome da empresa"
          />
          
          <EditableField
            label="CNPJ"
            value={company.cnpj ?? ''}
            fieldName="cnpj"
            isEditing={true}
            onEdit={() => handleEditField('cnpj')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ cnpj: text })}
            placeholder="00.000.000/0000-00"
          />
          
          <EditableField
            label="E-mail"
            value={company.email ?? ''}
            fieldName="email"
            isEditing={true}
            onEdit={() => handleEditField('email')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ email: text })}
            placeholder="contato@empresa.com"
            keyboardType="email-address"
          />
          
          <EditableField
            label="Telefone"
            value={company.telefone ?? ''}
            fieldName="telefone"
            isEditing={true}
            onEdit={() => handleEditField('telefone')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ telefone: text })}
            placeholder="(00) 00000-0000"
            keyboardType="phone-pad"
          />
          
          <EditableField
            label="CEP"
            value={company.cep ?? ''}
            fieldName="cep"
            isEditing={true}
            onEdit={() => handleEditField('cep')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ cep: text })}
            placeholder="00000-000"
            keyboardType="numeric"
          />
          
          <EditableField
            label="Cidade"
            value={company.cidade ?? ''}
            fieldName="cidade"
            isEditing={true}
            onEdit={() => handleEditField('cidade')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ cidade: text })}
            placeholder="Nome da cidade"
          />
          
          <EditableField
            label="UF"
            value={company.uf ?? ''}
            fieldName="uf"
            isEditing={true}
            onEdit={() => handleEditField('uf')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ uf: text })}
            placeholder="UF"
          />
          
          <EditableField
            label="Endereço"
            value={company.endereco ?? ''}
            fieldName="endereco"
            isEditing={true}
            onEdit={() => handleEditField('endereco')}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            onChangeText={(text) => companyUIActions.updateEditData({ endereco: text })}
            placeholder="Rua, número, complemento"
          />
        </View>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background.secondary,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  headerRight: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.md,
    overflow: 'hidden',
  },
  fieldRow: {
    flexDirection: 'row',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  fieldLabelContainer: {
    width: 80,
    justifyContent: 'center',
  },
  fieldLabel: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as any,
    color: theme.colors.text.secondary,
  },
  fieldValueContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  displayContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fieldValue: {
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    flex: 1,
  },
  editIconButton: {
    padding: theme.spacing.xs,
  },
  editContainer: {
    width: '100%',
  },
  input: {
    borderWidth: 1,
    borderColor: theme.colors.border.default,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.md,
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  editButton: {
    backgroundColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    marginLeft: theme.spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: theme.colors.text.tertiary,
  },
  editButtonText: {
    color: theme.colors.text.inverse,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium as any,
  },
  helpText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.text.tertiary,
    marginTop: theme.spacing.xs,
    fontStyle: 'italic',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 70,
    height: 70,
    borderRadius: 10,
  },
  logoPlaceholder: {
    backgroundColor: theme.colors.primary.light,
    justifyContent: 'center',
    alignItems: 'center',
  },
  changeLogoButton: {    
    borderWidth: 1,
    borderColor: theme.colors.primary.default,
    borderRadius: theme.borderRadius.sm,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
  },
  changeLogoText: {
    color: theme.colors.primary.default,
    fontSize: theme.fontSizes.xs,
    fontWeight: theme.fontWeights.medium as any,
  },
});

// ------------------------------------------------------------
// Helper (web-only) – compresses and resizes an image before it
// is stored/uploaded. Based on the implementation in
// ChecklistPhotoField.tsx.
// ------------------------------------------------------------
async function compressImageWeb(
  uri: string,
  options: { maxWidth?: number; quality?: number } = {}
): Promise<string> {
  const { maxWidth = 1280, quality = 0.75 } = options;

  logger.info(`[compressImageWeb] Iniciando compressão para uri=${uri}`);
  const startTs = Date.now();

  return new Promise((resolve, reject) => {
    const img: HTMLImageElement = document.createElement('img');
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      logger.info(`[compressImageWeb] Imagem carregada (${img.width}x${img.height})`);
      let { width, height } = img;
      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height = height * ratio;
        logger.info(`[compressImageWeb] Redimensionando para ${width}x${height}`);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const duration = Date.now() - startTs;
      logger.info(`[compressImageWeb] Concluído em ${duration}ms, tamanho ${(dataUrl.length / 1024).toFixed(1)}KB`);
      resolve(dataUrl.split(',')[1]);
    };

    img.onerror = (err) => {
      logger.error('[compressImageWeb] Erro ao carregar imagem', err);
      reject(err instanceof Error ? err : new Error('Image load error'));
    };

    img.src = uri;
  });
}

export default CompanyScreen; 
